/* eslint-disable no-use-before-define */
import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import {
  setDataToCookie,
  removeCookieByName,
  getDataFromCookieAToB,
  getDataFromCookie,
  removeAllCookie,
} from 'aster-foundation/core/common/utils/cookieHelper';
import { authUserLogOut } from 'aster-foundation/core/astercore/authService/redux/actions';
import { useDispatch } from 'react-redux';
import { errorHandling } from '@common/Utility/common-utils';
import { LOCALSTORAGE_KEYS } from '@common/Utility/constants';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

export const AuthTokenContext = createContext();

const AuthTokenProvider = ({ children }) => {
  const dispatch = useDispatch();
  const generateURL = useNavigationURL();
  const [shouldRerender, setShouldRerender] = useState(false);

  const setToken = (token) => {
    if (!token) return;

    removeCookieByName('Guest_Token');
    const encryptAccessToken = btoa(token?.accessToken);
    setDataToCookie('kcToken', encryptAccessToken, 0, false);
    // setDataToCookie('kcIdToken', encryptAccessToken, 0, false);
    setDataToCookie('kcRefreshToken', token?.refreshToken, 0);
    // setDataToCookie('kcClientId', token?.clientId);

    if (authCtx?.onTokenExpired) {
      const decodedToken = jwtDecode(token?.accessToken);

      // eslint-disable-next-line no-unsafe-optional-chaining
      const expiresIn = (decodedToken?.exp - new Date().getTime() / 1000) * 1000;
      if (authCtx.tokenTimeoutHandle) {
        clearTimeout(authCtx.tokenTimeoutHandle);
      }

      authCtx.tokenTimeoutHandle = setTimeout(authCtx.onTokenExpired, expiresIn);
    }
  };

  const login = (params) => {
    const { redirectUri } = params;
    window.location.assign(generateURL(`/core/login?redirectUri=${redirectUri}`));
  };

  const logout = (params) => {
    if (params?.callLogoutAPI && getDataFromCookie('kcRefreshToken')) {
      const requestParams = {
        refreshToken: getDataFromCookie('kcRefreshToken'),
      };

      dispatch(
        authUserLogOut({
          requestParams,
          onSuccess: () => {
            const isInstore = localStorage.getItem(LOCALSTORAGE_KEYS.INSTORE);
            localStorage.clear();
            sessionStorage.clear();
            removeAllCookie();
            if(isInstore){
              localStorage.setItem(LOCALSTORAGE_KEYS.INSTORE, isInstore);
              setShouldRerender(true);
            }

            if (params?.redirectUri) {
              window.location.assign(params.redirectUri);
            }
          },
          onFailure: (err) => {
            errorHandling(err);
          },
        }),
      );
    } else {
      removeCookieByName('kcToken');
      // removeCookieByName('kcIdToken');
      removeCookieByName('kcRefreshToken');
      removeCookieByName('userId');
      // removeCookieByName('kcClientId');

      if (params?.redirectUri) {
        window.location.assign(params.redirectUri);
      }
    }
  };

  const navigateToRedirectUri = (redirectUri) => {
    window.location.assign(redirectUri || '/');
  };

  const isTokenValid = (accessToken) => {
    let kcToken = null;
    if (!accessToken) kcToken = typeof window !== 'undefined' ? getDataFromCookieAToB('kcToken') : null;
    if (typeof window !== 'undefined') {
      const refreshToken = getDataFromCookie('kcRefreshToken');
      if (!refreshToken) {
        logout();
        return false;
      }
    }

    if (kcToken) {
      const decodedToken = jwtDecode(kcToken);
      try {
        const tokenHostURL = new URL(decodedToken?.iss);
        if (tokenHostURL?.host.indexOf('myaster.com') < -1) {
          logout();
          return false;
        }
      } catch {
        logout();
        return false;
      }

      const tokenExpiryTimeStamp = decodedToken.exp;
      const currentTimeStamp = Math.floor(new Date() / 1000);
      return tokenExpiryTimeStamp > currentTimeStamp;
    }

    return false;
  };

  const kcToken = typeof window !== 'undefined' ? getDataFromCookieAToB('kcToken') : null;
  const decodedToken = kcToken && jwtDecode(kcToken);
  const authCtx = {
    accessToken: kcToken || null,
    idTokenParsed: decodedToken,
    initialized: true,
    authenticated: isTokenValid(),
    setToken,
    login,
    logout,
    onTokenExpired: () => {},
    isTokenValid,
    navigateToRedirectUri,
  };

  useEffect(() => {
    const expiresIn = (authCtx?.idTokenParsed?.exp - new Date().getTime() / 1000) * 1000;
    const timeoutId = setTimeout(
      (timeoutPeriod) => {
        if (Number.isNaN(timeoutPeriod)) return;
        if (authCtx.onTokenExpired) {
          authCtx?.onTokenExpired();
        }
      },
      expiresIn,
      expiresIn,
    );
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <AuthTokenContext.Provider value={{ authCtx, shouldRerender, initialized: true }}>{children}</AuthTokenContext.Provider>
  );
};

AuthTokenProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default React.memo(AuthTokenProvider);
